import { draftKey, editableTouchpoint } from './touchpoints.ts';

type Log = { id: number; title: string; type: string; status: string; person_id?: number | null };

export function deletableTouchpoint(row: Log | null | undefined): row is Log {
  return !!row && row.id > 0 && row.status === 'completed' && editableTouchpoint(row);
}

/** Capture the target before confirmation. The server remains the access authority. */
export function createTouchpointDeleter(deps: {
  confirm: (message: string) => Promise<boolean>;
  remove: (id: number) => Promise<unknown>;
  invalidate: () => Promise<unknown>;
  removeDraft: (key: string) => void;
  setBusy: (busy: boolean) => void;
  setError: (error: unknown | null) => void;
}) {
  let busy = false;
  return async (row: Log | null | undefined, userId: number, context: { isCurrent: () => boolean; onDeleted: () => void }) => {
    if (busy || !deletableTouchpoint(row)) return;
    const { id, title, person_id } = row;
    busy = true;
    deps.setBusy(true);
    deps.setError(null);
    try {
      const accepted = await deps.confirm(`Delete manual log #${id} “${title}”? This removes this recorded touchpoint from the log and timeline. It does not delete the contact, company or any other logs. This is not cancelling a plan.`);
      if (!accepted || !context.isCurrent()) return;
      await deps.remove(id);
      if (person_id) {
        const key = draftKey(userId, person_id, id);
        for (const suffix of ['', ':log', ':plan']) {
          try { deps.removeDraft(key + suffix); } catch { /* Storage may be unavailable. */ }
        }
      }
      if (context.isCurrent()) context.onDeleted();
      // All shared views, counts, summaries and My Day depend on this record.
      await deps.invalidate();
    } catch (err) {
      if (context.isCurrent()) deps.setError(err);
    } finally {
      busy = false;
      deps.setBusy(false);
    }
  };
}
